// src/api/toolService.js
import apiClient from './axiosConfig';
// Assuming modelService is in the same directory or adjust path
// We don't need modelService here, but ToolEditor.js will.

const API_URL = process.env.REACT_APP_API_URL || '';

const handleApiError = (error, operation) => {
  console.error(`${error.config?.method?.toUpperCase()} ${error.config?.url} - Error ${operation}:`, error);
  let message = 'An unknown error occurred.';
  const status = error.response?.status;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
        message = d.detail;
    } else if (Array.isArray(d.detail)) {
        message = d.detail.map(err => {
            const loc = err.loc?.slice(1).join('.') || 'field';
            return `${loc}: ${err.msg}`;
        }).join('; ');
         if (!message) message = "Validation error";
    } else if (typeof d === 'string' && d.length < 500) {
        message = d;
    } else if (d.message && typeof d.message === 'string') {
        message = d.message;
    } else if (status) {
         message = `Request failed with status code ${status}`;
    }
     if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Request failed with status code ${status || 'unknown'}`;
     }
  } else if (error.message) {
    message = error.message;
  }

  return {
    isError: true,
    message,
    status: status,
    originalError: error
  };
};

export const toolService = {
  /** GET /api/v1/tools - List all tools (user's and platform) */
  getAllTools: async () => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/tools`);
      return data;
    } catch (err) {
      return handleApiError(err, 'fetching tools');
    }
  },

  /** GET /api/v1/tools/{tool_uuid} - Get a single tool by its UUID */
  getToolById: async (toolUuid) => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/tools/${toolUuid}`);
      return data;
    } catch (err) {
      return handleApiError(err, `fetching tool ${toolUuid}`);
    }
  },

  /** POST /api/v1/tools - Create a new tool */
  createTool: async (toolData) => {
    try {
      const payload = {
        // MODIFIED HERE: 'id' changed to 'slug'
        // toolData.id from ToolEditor already contains the slug value
        slug: toolData.id, 
        name: toolData.name,
        description: toolData.description,
        tool_type: toolData.tool_type,
        enabled: toolData.enabled ?? true,
        default_params: toolData.default_params ?? {},
        code: toolData.code ?? '', // Code is now part of the main tool data
        requires_dedicated_agent: toolData.requires_dedicated_agent ?? false,
        dedicated_llm_model: toolData.dedicated_llm_model || null,
        dedicated_system_prompt: toolData.dedicated_system_prompt || null,
        agent_config: toolData.agent_config || {},
      };
      const { data } = await apiClient.post(`${API_URL}/api/v1/tools`, payload);
      return data;
    } catch (err) {
      return handleApiError(err, 'creating tool');
    }
  },

  /** PUT /api/v1/tools/{tool_uuid} - Update tool metadata AND code */
  updateTool: async (toolUuid, toolUpdateData) => {
    try {
      // Backend expects all fields for PUT on /api/v1/tools/{tool_uuid}
      // if we intend to update code and metadata together.
      // OR, we stick to the API doc:
      // PUT /api/v1/tools/{tool_uuid} for metadata
      // PUT /api/v1/tools/{tool_uuid}/code for code
      // For MVP, let's assume the main updateTool updates metadata,
      // and code is updated via updateToolCode as per API doc.
      // The ToolEditor handleSubmit will call both if code has changed.
      const allowedMetadataUpdateFields = [
        'name', 'description', 'enabled', 'default_params', 'tool_type',
        'requires_dedicated_agent', 'dedicated_llm_model',
        'dedicated_system_prompt', 'agent_config',
      ];
      const payload = {};
      for (const field of allowedMetadataUpdateFields) {
        if (toolUpdateData.hasOwnProperty(field)) {
          if (field === 'dedicated_llm_model' || field === 'dedicated_system_prompt') {
            payload[field] = toolUpdateData[field] === undefined ? null : toolUpdateData[field];
          } else {
            payload[field] = toolUpdateData[field];
          }
        }
      }
      // if (Object.keys(payload).length === 0 && !Object.values(payload).some(v => v === null && toolUpdateData.hasOwnProperty(Object.keys(payload).find(k => payload[k] === v)))) {
      //    console.warn("updateTool (metadata) called with no valid updatable fields for tool:", toolUuid, payload);
      // }

      const { data } = await apiClient.put(
        `${API_URL}/api/v1/tools/${toolUuid}`,
        payload
      );
      return data;
    } catch (err) {
      return handleApiError(err, `updating tool metadata ${toolUuid}`);
    }
  },

  /** DELETE /api/v1/tools/{tool_uuid} - Delete a tool by its UUID */
  deleteTool: async (toolUuid) => {
    try {
      await apiClient.delete(`${API_URL}/api/v1/tools/${toolUuid}`);
      return { success: true };
    } catch (err) {
      return handleApiError(err, `deleting tool ${toolUuid}`);
    }
  },

  /** GET /api/v1/tools/{tool_uuid}/code - Fetch the Python source code for a tool */
  getToolCode: async (toolUuid) => {
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/tools/${toolUuid}/code`
      );
      return data; // Expected: { code: string }
    } catch (err) {
      return handleApiError(err, `fetching code for tool ${toolUuid}`);
    }
  },

  /** PUT /api/v1/tools/{tool_uuid}/code - Overwrite a tool’s Python source code */
  updateToolCode: async (toolUuid, code) => {
    try {
      const { data } = await apiClient.put(
        `${API_URL}/api/v1/tools/${toolUuid}/code`,
        { code }, // Body is { "code": "python code..." }
        { headers: { 'Content-Type': 'application/json' } }
      );
      return data; // Expected: { code: string }
    } catch (err) {
      return handleApiError(err, `updating code for tool ${toolUuid}`);
    }
  },

  /** Enable a tool by its UUID */
  enableTool: async (toolUuid) => {
    try {
      // This calls updateTool which only sends allowed metadata fields
      const result = await toolService.updateTool(toolUuid, { enabled: true });
      return result;
    } catch (err) {
      return err.isError ? err : handleApiError(err, `enabling tool ${toolUuid}`);
    }
  },

  /** Disable a tool by its UUID */
  disableTool: async (toolUuid) => {
    try {
      const result = await toolService.updateTool(toolUuid, { enabled: false });
      return result;
    } catch (err) {
      return err.isError ? err : handleApiError(err, `disabling tool ${toolUuid}`);
    }
  },

  // clearAgentCache was confirmed to be removed as it's not in API docs.
};